import React, { useState } from 'react';
import { Phone, Mail, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { contactUs } from '../assets/images';
import useFormSubmit from '../hooks/useFormSubmit';

const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    firstName: '',
    email: '',
    phone: '',
    message: ''
  });
  
  // Form submission hook - use the Cloudflare Worker endpoint
  const { 
    submitForm, 
    isSubmitting, 
    isSuccess, 
    isError, 
    message,
    resetForm
  } = useFormSubmit(); // No need to specify URL here, it will use the environment variable

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-primary-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-r from-secondary-500/30 to-accent-500/30 blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-full h-64 bg-gradient-to-l from-secondary-500/30 to-accent-500/30 blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {t('contact.getInTouch')}
            </motion.h1>
            <motion.p 
              className="text-xl text-gray-200 mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {t('contact.reachOut')}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <img 
                  src={contactUs}
                  alt="Contact illustration"
                  className="w-full h-auto max-w-lg mx-auto"
                />
              </motion.div>
            </div>
            
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-primary-950">{t('contact.getInTouch')}</h2>
              <p className="text-lg text-gray-600">
                {t('contact.reachOut')}
              </p>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <Phone className="text-black" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold">{t('contact.phone')}</h3>
                    <p className="text-gray-600">450 805-3277</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <Mail className="text-black" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold">{t('contact.email')}</h3>
                    <p className="text-gray-600">info@financesunshine.com</p>
                  </div>
                </div>                
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold text-primary-950 mb-6">{t('contact.sendMessage')}</h2>
              <form className="space-y-6" onSubmit={async (e) => {
                e.preventDefault();
                await submitForm(formData);
              }}>
                {/* Success message */}
                {isSuccess && (
                  <div className="bg-green-50 text-green-800 p-4 rounded-md flex items-start space-x-3">
                    <CheckCircle className="flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <p className="font-medium">{message}</p>
                      <button 
                        className="text-green-700 underline text-sm mt-2"
                        onClick={() => {
                          resetForm();
                          setFormData({
                            name: '',
                            firstName: '',
                            email: '',
                            phone: '',
                            message: ''
                          });
                        }}
                      >
                        {t('contact.sendAnother')}
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Error message */}
                {isError && (
                  <div className="bg-red-50 text-red-800 p-4 rounded-md flex items-start space-x-3">
                    <AlertCircle className="flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <p className="font-medium">{message || t('contact.errorMessage')}</p>
                      <button 
                        className="text-red-700 underline text-sm mt-2"
                        onClick={resetForm}
                      >
                        {t('contact.tryAgain')}
                      </button>
                    </div>
                  </div>
                )}
                
                {!isSuccess && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact.name')}*</label>
                        <input 
                          type="text" 
                          name="name"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact.firstName')}*</label>
                        <input 
                          type="text" 
                          name="firstName"
                          value={formData.firstName}
                          onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact.email')}*</label>
                      <input 
                        type="email" 
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact.phone')}</label>
                      <input 
                        type="tel" 
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact.message')}*</label>
                      <textarea 
                        name="message"
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black resize-none"
                        required
                        rows={5}
                      ></textarea>
                    </div>
                    <button 
                      type="submit" 
                      className="inline-flex items-center px-8 py-3.5 bg-black hover:bg-gray-900 text-white font-medium rounded-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t('contact.sending')}
                        </span>
                      ) : (
                        <>
                          <span>{t('contact.sendMessage')}</span>
                          <ArrowRight size={20} className="ml-2" />
                        </>
                      )}
                    </button>
                  </>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;